/* eslint-disable no-case-declarations */
import {
  IonContent,
  IonHeader,
  IonPage,
  IonToolbar,
  IonButtons,
  IonBackButton,
  useIonActionSheet,
  useIonToast,
  isPlatform
} from '@ionic/react';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource, PermissionStatus } from '@capacitor/camera';
import { Box, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Menu, MenuItem, Chip, Divider, Paper, Avatar } from '@mui/material';
import {
  Message,
  Flag,
  AccountCircle,
  CameraAlt,
  Panorama,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  MoreVert as MoreVertIcon,
  LocationOn,
  Business as BusinessIcon,
  Work as WorkIcon,
  Campaign as CampaignIcon,
  Category as CategoryIcon,
  Info
} from '@mui/icons-material';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { collection, getDocs, query, where, doc, updateDoc, getDoc, setDoc, addDoc, Timestamp, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Post } from '../types/post';
import { BaseUser, Company, Contractor, Ambassador } from '../types/user';
import { PostCard } from '../components/PostCard';
import { useAuthContext } from '../contexts/AuthContext';

export const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const { userData: currentUser, user: authUser, isLoading } = useAuthContext();
  const [user, setUser] = useState<BaseUser | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [displayCount, setDisplayCount] = useState(10);
  const [presentActionSheet] = useIonActionSheet();
  const [presentToast] = useIonToast();
  const history = useHistory();
  const location = useLocation();
  // Removed refs since we're using Camera API directly
  const [deletePostId, setDeletePostId] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [isUploadingProfile, setIsUploadingProfile] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);

  const isOwnProfile = location.pathname === '/my-profile';
  const targetUserId = isOwnProfile ? authUser?.uid : id;

  useEffect(() => {
    const fetchUser = async () => {
      if (!targetUserId) return;
      
      try {
        const userRef = doc(db, 'users', targetUserId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
          presentToast({
            message: 'User not found',
            duration: 2000,
            color: 'danger'
          });
          if (isOwnProfile) {
            history.replace('/register');
          }
          return;
        }
        
        const data = userDoc.data();
        // Safely convert Firestore Timestamps
        const userData = {
          ...data,
          id: targetUserId,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        };
        
        setUser(userData as BaseUser);

        // Create profile visit notification if not own profile and visitor is not the profile owner
        if (!isOwnProfile && currentUser && targetUserId && currentUser.id !== targetUserId) {
          try {
            // Check notification settings
            const settingsDoc = await getDoc(doc(db, 'notificationSettings', targetUserId));
            const settings = settingsDoc.exists() ? settingsDoc.data() : { profileVisits: true };
            
            if (settings.profileVisits) {
              let userName = 'Unknown User';
              
              if (currentUser.type === 'company') {
                userName = (currentUser as Company).companyName || 'Company';
              } else if (currentUser.type === 'contractor' || currentUser.type === 'ambassador') {
                const firstName = (currentUser as Contractor | Ambassador).firstName || '';
                const lastName = (currentUser as Contractor | Ambassador).lastName || '';
                userName = `${firstName} ${lastName}`.trim();
                
                if (currentUser.type === 'ambassador' && (currentUser as Ambassador).username) {
                  userName = (currentUser as Ambassador).username;
                }
              }
              
              if (!userName || userName === 'Unknown User') {
                userName = currentUser.id || 'Unknown User';
              }



              try {
                // Check if a similar notification was created in the last hour to avoid duplicates
                const oneHourAgo = new Date();
                oneHourAgo.setHours(oneHourAgo.getHours() - 1);
                
                const recentNotificationsQuery = query(
                  collection(db, 'notifications'),
                  where('type', '==', 'profile_visit'),
                  where('fromUserId', '==', currentUser.id),
                  where('toUserId', '==', targetUserId),
                  where('createdAt', '>=', oneHourAgo)
                );
                
                const recentNotifications = await getDocs(recentNotificationsQuery);
                
                // Only create a new notification if there isn't a recent one
                if (recentNotifications.empty) {
                  try {
                    // Create a profile visit document in the profileVisits collection
                    await addDoc(collection(db, 'profileVisits'), {
                      fromUserId: currentUser.id,
                      fromUserName: userName,
                      toUserId: targetUserId,
                      read: false,
                      createdAt: new Date()
                    });
                  } catch (permissionError) {
                    // Silently handle permission errors - profile visits are optional
                    // The user can still view the profile without this feature
                  }
                }
              } catch (visitorError) {
                // Silently handle all errors - profile visits are optional
                // The main functionality (viewing profiles) continues to work
              }
            }
          } catch (error) {
            console.error('Error creating profile visit notification:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        if (isOwnProfile) {
          history.replace('/home');
        }
      }
    };

    fetchUser();
  }, [id, currentUser, authUser, isOwnProfile, targetUserId, presentToast, history, t]);



  useEffect(() => {
    const fetchPosts = async () => {
      if (user?.id) {
        try {
          // Query posts using both authorId and userId since existing posts might use either
          const [authorPosts, userPosts] = await Promise.all([
            getDocs(query(collection(db, 'posts'), where('authorId', '==', user.id))),
            getDocs(query(collection(db, 'posts'), where('userId', '==', user.id)))
          ]);
          
          // Combine and deduplicate posts
          const postsMap = new Map();
          
          [...authorPosts.docs, ...userPosts.docs].forEach(doc => {
            if (!postsMap.has(doc.id)) {
              const data = doc.data();
              postsMap.set(doc.id, {
                id: doc.id,
                ...data,
                // Ensure author info is present
                authorId: data.authorId || data.userId,
                authorName: data.authorName || getUserName(user),
                authorPhotoURL: data.authorPhotoURL || user.profilePhotoUrl || '',
                createdAt: data.createdAt?.toDate?.() || new Date(),
                updatedAt: data.updatedAt?.toDate?.() || new Date(),
                likedBy: data.likedBy || [],
                likes: data.likes || 0,
                comments: data.comments || []
              } as Post);
            }
          });
          
          const postsData = Array.from(postsMap.values()) as Post[];
          
          // Sort posts by creation date, newest first
          postsData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
          setPosts(postsData);
        } catch (error) {
          console.error('Error fetching posts:', error);
          presentToast({
            message: t('profile.error_loading_posts'),
            duration: 2000,
            color: 'danger'
          });
        }
      }
    };

    if (user) {
      fetchPosts();
    }
  }, [user, t, presentToast]);

  const handlePhotoUpload = async (file: File, type: 'profile' | 'cover') => {
    if (!authUser?.uid) {
      presentToast({
        message: 'Unable to update photo: User ID not found',
        duration: 2000,
        color: 'danger'
      });
      return;
    }

    // Set loading state based on photo type
    if (type === 'profile') {
      setIsUploadingProfile(true);
    } else {
      setIsUploadingCover(true);
    }

    try {
      // Show toast to indicate upload has started
      presentToast({
        message: t('profile.photo_uploading'),
        duration: 2000,
        color: 'primary'
      });

      // Upload photo to storage
      const storageRef = ref(storage, `users/${authUser.uid}/${type}-photo`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      // Update user document
      const userRef = doc(db, 'users', authUser.uid);
      const updateData = type === 'profile' 
        ? { profilePhotoUrl: url }
        : { backgroundPhotoUrl: url };
      
      await updateDoc(userRef, updateData);

      // Update local state
      setUser(prev => prev ? { ...prev, ...updateData } : prev);

      presentToast({
        message: t('profile.photo_updated'),
        duration: 2000,
        color: 'success'
      });
    } catch (error) {
      console.error('Error uploading photo:', error);
      presentToast({
        message: t('profile.photo_update_error'),
        duration: 2000,
        color: 'danger'
      });
    } finally {
      // Reset loading state
      if (type === 'profile') {
        setIsUploadingProfile(false);
      } else {
        setIsUploadingCover(false);
      }
    }
  };

  const handlePhotoCapture = async (type: 'profile' | 'cover') => {
    if (!isOwnProfile || !authUser?.uid) return;
    
    try {
      // Check if we're on a native platform (iOS/Android)
      if (Capacitor.isNativePlatform()) {
        // Check permission status first
        const permissionStatus = await Camera.checkPermissions();
        
        // If permission is not granted, request it
        if (permissionStatus.photos !== 'granted') {
          const requestResult = await Camera.requestPermissions({
            permissions: ['photos']
          });
          
          // If still not granted after request, show error
          if (requestResult.photos !== 'granted') {
            presentToast({
              message: t('profile.gallery_permission_denied'),
              duration: 3000,
              color: 'warning'
            });
            return;
          }
        }
      }
      
      // Use Camera API to get photo from gallery
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.Uri,
        source: CameraSource.Photos
      });
      
      // Convert image to file
      const response = await fetch(image.webPath!);
      const blob = await response.blob();
      const file = new File([blob], `${type}-photo.jpg`, { type: 'image/jpeg' });
      
      // Upload the file
      await handlePhotoUpload(file, type);
      
    } catch (error) {
      console.error('Error capturing photo:', error);
      
      // Check if the error is related to permissions
      if (error.message && (
          error.message.includes('permission') ||
          error.message.includes('denied') ||
          error.message.includes('access')
      )) {
        presentToast({
          message: t('profile.gallery_permission_denied'),
          duration: 3000,
          color: 'warning'
        });
      } else {
        presentToast({
          message: t('profile.photo_capture_error'),
          duration: 2000,
          color: 'danger'
        });
      }
    }
  };

  const handleProfilePhotoClick = () => {
    if (isOwnProfile) {
      handlePhotoCapture('profile');
    }
  };

  const handleCoverPhotoClick = () => {
    if (isOwnProfile) {
      handlePhotoCapture('cover');
    }
  };

  const handleMessage = async () => {
    if (!authUser?.uid || !targetUserId || !user) return;

    try {
      // Use targetUserId instead of user.id to ensure we're messaging the correct person
      const otherUserId = targetUserId;
      
      // Check if conversation already exists
      const conversationsRef = collection(db, 'conversations');
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', authUser.uid)
      );
      const querySnapshot = await getDocs(q);
      
      // eslint-disable-next-line prefer-const
      let existingConversation = querySnapshot.docs.find(doc => {
        const data = doc.data();
        return data.participants.includes(otherUserId);
      });

      let conversationId;
      
      if (existingConversation) {
        conversationId = existingConversation.id;
      } else {
        // Create new conversation
        const newConversationRef = await addDoc(conversationsRef, {
          participants: [authUser.uid, otherUserId],
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
        conversationId = newConversationRef.id;
      }

      // Navigate to messages page with the conversation selected
      history.push(`/messages?conversation=${conversationId}`);
    } catch (error) {
      console.error('Error handling message:', error);
      presentToast({
        message: 'Error starting conversation',
        duration: 2000,
        color: 'danger'
      });
    }
  };

  const handleReport = () => {
    presentActionSheet({
      header: t('profile.report_header'),
      buttons: [
        {
          text: t('profile.report_inappropriate'),
          handler: () => {
            // TODO: Implement report logic
            presentToast({
              message: t('profile.report_submitted'),
              duration: 2000,
              color: 'success'
            });
          }
        },
        {
          text: t('profile.report_spam'),
          handler: () => {
            // TODO: Implement report logic
            presentToast({
              message: t('profile.report_submitted'),
              duration: 2000,
              color: 'success'
            });
          }
        },
        {
          text: t('common.cancel'),
          role: 'cancel'
        }
      ]
    });
  };

  const handleEditPost = (post: Post) => {
    history.push(`/edit-post/${post.id}`);
  };

  const handleDeletePost = async () => {
    if (!deletePostId) return;

    try {
      await deleteDoc(doc(db, 'posts', deletePostId));
      setPosts(prev => prev.filter(post => post.id !== deletePostId));
      setDeletePostId(null);
      presentToast({
        message: t('post.delete_success'),
        duration: 2000,
        color: 'success',
        position: 'bottom'
      });
    } catch (error) {
      console.error('Error deleting post:', error);
      presentToast({
        message: t('post.delete_error'),
        duration: 2000,
        color: 'danger',
        position: 'bottom'
      });
    }
  };

  const getUserName = (user: BaseUser | null) => {
    if (!user) return 'User';
    try {
      if (!user.type) return 'User';
      
      switch (user.type) {
        case 'company':
          const company = user as Company;
          return company?.companyName || 'Company';
        case 'contractor':
          const contractor = user as Contractor;
          if (contractor?.firstName && contractor?.lastName) {
            return `${contractor.lastName.charAt(0)}. ${contractor.firstName}`;
          }
          return 'User';
        case 'ambassador':
          const ambassador = user as Ambassador;
          if (ambassador?.username) {
            return ambassador.username;
          }
          if (ambassador?.firstName && ambassador?.lastName) {
            return `${ambassador.firstName} ${ambassador.lastName}`;
          }
          return 'User';
        default:
          return 'User';
      }
    } catch (error) {
      console.error('Error getting user name:', error);
      return 'User';
    }
  };

  const getCustomFieldValue = (field: { isOther: boolean; value: string; customValue: string | null } | string | undefined): string => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field.isOther ? (field.customValue || '') : field.value;
  };

  const getActivityDisplay = (user: BaseUser | null): string => {
    if (!user) return '';
    try {
      switch (user.type) {
        case 'company': {
          const company = user as Company;
          // Get industry value and ensure it's lowercase before translation
          const industryValue = getCustomFieldValue(company.industry);
          const industry = industryValue ? t(`registration.industries.${industryValue.toLowerCase()}`) : '';
          const specialization = company.specialization || '';
          return specialization ? `${industry} - ${specialization}` : industry;
        }
        case 'contractor': {
          const contractor = user as Contractor;
          // Get service type value and ensure it's lowercase before translation
          const serviceTypeValue = getCustomFieldValue(contractor.serviceType);
          const serviceType = serviceTypeValue ? t(`registration.industries.${serviceTypeValue.toLowerCase()}`) : '';
          const specialization = contractor.specialization || '';
          return specialization ? `${serviceType} - ${specialization}` : serviceType;
        }
        case 'ambassador': {
          const ambassador = user as Ambassador;
          // Get activity type value and ensure it's lowercase before translation
          const activityTypeValue = getCustomFieldValue(ambassador.activityType);
          return activityTypeValue ? t(`registration.activityTypes.${activityTypeValue.toLowerCase()}`) : '';
        }
        default:
          return '';
      }
    } catch (error) {
      return '';
    }
  };

  const getUserDescription = (user: BaseUser | null): string => {
    if (!user) return 'No description available';
    try {
      switch (user.type) {
        case 'company':
          return (user as Company).description || 'No description available';
        case 'contractor':
          return (user as Contractor).servicesDescription || 'No services description available';
        case 'ambassador': {
          const ambassador = user as Ambassador;
          const specialization = getCustomFieldValue(ambassador.ambassadorSpecialization);
          return ambassador.ambassadorDescription || specialization || 'No description available';
        }
        default:
          return 'No description available';
      }
    } catch (error) {
      return 'No description available';
    }
  };

  if (isLoading) {
    return (
      <IonPage>
        <IonContent className="ion-padding-bottom">
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography>{t('common.loading')}</Typography>
          </Box>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar className="glass-effect" style={{ 
          '--background': 'transparent',
          '--border-width': '0',
          '--padding-top': '10px',
          '--padding-bottom': '10px'
        }}>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/home" />
          </IonButtons>
          <Typography 
            variant="h6" 
            sx={{ 
              textAlign: 'center', 
              flexGrow: 1, 
              fontWeight: 700,
              letterSpacing: '-0.5px',
              color: '#0068B9'
            }}
          >
            {getUserName(user) || t('profile.profile')}
          </Typography>
          <IonButtons slot="end">
            {isOwnProfile && (
              <IconButton 
                onClick={() => history.push('/settings')} 
                sx={{ 
                  mr: 1, 
                  background: 'rgba(255, 255, 255, 0.8)',
                  boxShadow: '0 4px 12px rgba(0, 104, 185, 0.1)',
                  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 16px rgba(0, 104, 185, 0.15)'
                  }
                }}
              >
                <SettingsIcon sx={{ color: '#0068B9' }} />
              </IconButton>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <Box sx={{ position: 'relative', mb: 8 }}>
          <Box
            sx={{
              width: '100%',
              height: 200,
              bgcolor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isOwnProfile && !isUploadingCover ? 'pointer' : 'default',
              position: 'relative'
            }}
            onClick={!isUploadingCover ? handleCoverPhotoClick : undefined}
          >
            {isUploadingCover && (
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  bottom: 0, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  zIndex: 2
                }}
              >
                <Typography color="white" sx={{ fontWeight: 'bold' }}>
                  {t('profile.uploading')}...
                </Typography>
              </Box>
            )}
            
            {user?.backgroundPhotoUrl ? (
              <img
                src={user.backgroundPhotoUrl}
                alt={t('profile.cover_photo_alt')}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  opacity: isUploadingCover ? 0.5 : 1
                }}
              />
            ) : (
              <Panorama sx={{ fontSize: 64, color: 'grey.400' }} />
            )}
            
            {isOwnProfile && !isUploadingCover && (
              <IconButton
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  bgcolor: 'background.paper',
                  zIndex: 1
                }}
              >
                <CameraAlt />
              </IconButton>
            )}
          </Box>

          <Box
            sx={{
              position: 'absolute',
              left: 16,
              bottom: -64,
              width: 128,
              height: 128,
              borderRadius: '50%',
              bgcolor: 'grey.200',
              border: 4,
              borderColor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isOwnProfile && !isUploadingProfile ? 'pointer' : 'default',
              overflow: 'hidden'
            }}
            onClick={!isUploadingProfile ? handleProfilePhotoClick : undefined}
          >
            {isUploadingProfile && (
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  bottom: 0, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  zIndex: 2,
                  borderRadius: '50%'
                }}
              >
                <Typography color="white" sx={{ fontWeight: 'bold', fontSize: '0.7rem', textAlign: 'center' }}>
                  {t('profile.uploading')}...
                </Typography>
              </Box>
            )}
            
            {user?.profilePhotoUrl ? (
              <img
                src={user.profilePhotoUrl}
                alt={t('profile.profile_photo_alt')}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '50%',
                  opacity: isUploadingProfile ? 0.5 : 1
                }}
              />
            ) : (
              <AccountCircle sx={{ fontSize: 64, color: 'grey.400' }} />
            )}
            
            {isOwnProfile && !isUploadingProfile && (
              <IconButton
                sx={{
                  position: 'absolute',
                  right: 0,
                  bottom: 0,
                  bgcolor: 'background.paper',
                  zIndex: 1
                }}
              >
                <CameraAlt />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Removed file inputs since we're now using Camera API directly */}

        <Box sx={{ px: 2, pb: 8 }}>
          {!isOwnProfile && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<Message />}
                onClick={handleMessage}
              >
                {t('profile.message')}
              </Button>
            </Box>
          )}

          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
            {getUserName(user)}
          </Typography>

          <Box className="stagger-children">
            {/* User Type */}
            {user && user.type && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar
                  src={
                    user.type === 'company' ? '/assets/image/types/Entreprise.png' :
                    user.type === 'contractor' ? '/assets/image/types/Prestataire.png' :
                    user.type === 'ambassador' ? '/assets/image/types/Affiliateur.png' :
                    '/assets/image/logo.png'
                  }
                  sx={{
                    width: 24,
                    height: 24,
                    mr: 1
                  }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                  {t(`auth.${user.type}`)}
                </Typography>
              </Box>
            )}
            
            {/* Location */}
            {user && user.city && user.country && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocationOn color="primary" fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {user.city}, {user.country}
                </Typography>
              </Box>
            )}

            {/* Activity */}
            {user && getActivityDisplay(user) && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <CategoryIcon color="primary" fontSize="small" sx={{ mr: 1, mt: 0.5 }} />
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  {getActivityDisplay(user)}
                </Typography>
              </Box>
            )}

            {/* Description */}
            <Paper 
              elevation={1}
              sx={{ 
                p: 3, 
                mb: 4, 
                borderRadius: '20px',
                bgcolor: 'white'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Info color="primary" sx={{ mt: 0.5 }} />
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  {String(getUserDescription(user))}
                </Typography>
              </Box>
            </Paper>
          </Box>

          {/* Posts Section */}
          <Typography 
            variant="h6" 
            sx={{ 
              background: 'transparent',
              textAlign: 'center', 
              paddingBottom: 2,
              flexGrow: 1, 
              fontWeight: 700,
              letterSpacing: '-0.5px',
              color: '#0068B9'
            }}
          >
            {t('profile.posts')}
          </Typography>
          
          {posts.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {posts.slice(0, displayCount).map(post => (
                <Box key={post.id} sx={{ position: 'relative', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                  <PostCard 
                    post={post}
                    currentUserId={authUser?.uid}
                  />
                  {isOwnProfile && (
                    <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          setMenuAnchorEl(event.currentTarget);
                          setSelectedPost(post);
                        }}
                        sx={{ bgcolor: 'background.paper' }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              ))}
              
              {posts.length > displayCount && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Button 
                    variant="contained"
                    color="primary"
                    onClick={() => setDisplayCount(prev => prev + 10)}
                    sx={{ 
                      borderRadius: '16px',
                      textTransform: 'none'
                    }}
                  >
                    {t('profile.show_more')}
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <Paper 
              elevation={1}
              sx={{ 
                textAlign: 'center', 
                py: 4, 
                px: 3,
                borderRadius: '20px',
                bgcolor: 'white'
              }}
            >
              <Avatar 
                sx={{ 
                  width: 60, 
                  height: 60, 
                  mx: 'auto', 
                  mb: 2, 
                  bgcolor: 'primary.light' 
                }}
              >
                <AddIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {t('profile.no_posts')}
              </Typography>
              {isOwnProfile && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => history.push('/new-post')}
                  sx={{
                    borderRadius: '16px',
                    textTransform: 'none'
                  }}
                >
                  {t('profile.create_post')}
                </Button>
              )}
            </Paper>
          )}
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={!!deletePostId}
          onClose={() => setDeletePostId(null)}
        >
          <DialogTitle>{t('post.delete_confirm_title')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('post.delete_confirm_message')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeletePostId(null)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleDeletePost} color="error" variant="contained">
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Post Actions Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={() => {
            setMenuAnchorEl(null);
            setSelectedPost(null);
          }}
        >
          <MenuItem
            onClick={() => {
              if (selectedPost) {
                handleEditPost(selectedPost);
                setMenuAnchorEl(null);
                setSelectedPost(null);
              }
            }}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            {t('common.edit')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (selectedPost) {
                setDeletePostId(selectedPost.id);
                setMenuAnchorEl(null);
                setSelectedPost(null);
              }
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            {t('common.delete')}
          </MenuItem>
        </Menu>
      </IonContent>
    </IonPage>
  );
};
